/**
 * The purpose of this feature is to force the application to reload after a prolonged period of user inactivity.
 * Failure to reload causes external links to SigView to expire and the application itself to behave unpredictably.
 */
import { useCallback, useEffect, useRef } from 'react';

export const DEFAULT_RELOAD_INTERVAL = 8 * 60 * 60 * 1000;

type UseAppReloadOptions = {
  interval?: number;
  enabled?: boolean;
  onBeforeReload?: () => void;
};

/**
 * Hook to automatically reload the app at specified intervals
 * @param options Configuration options for the auto-reload
 */
export const useAppReload = ({
  interval = DEFAULT_RELOAD_INTERVAL,
  enabled = true,
  onBeforeReload,
}: UseAppReloadOptions = {}) => {
  const onBeforeReloadRef = useRef(onBeforeReload);
  const intervalRef = useRef(interval);
  const timerIdRef = useRef<NodeJS.Timeout | null>(null);
  const isReloadingRef = useRef(false);

  onBeforeReloadRef.current = onBeforeReload;
  intervalRef.current = interval;

  const scheduleReload = useCallback(() => {
    if (timerIdRef.current) {
      clearTimeout(timerIdRef.current);
    }

    timerIdRef.current = setTimeout(() => {
      if (isReloadingRef.current) {
        return;
      }

      isReloadingRef.current = true;

      try {
        if (onBeforeReloadRef.current) {
          onBeforeReloadRef.current();
        }
        window.location.reload();
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: logging error in console
        console.error('Error during app reload:', error);
      }
    }, intervalRef.current);
  }, []);

  const resetAndRestartTimer = () => {
    if (timerIdRef.current) {
      clearTimeout(timerIdRef.current);
      timerIdRef.current = null;
    }

    isReloadingRef.current = false;
    scheduleReload();
  };

  useEffect(() => {
    if (!enabled || timerIdRef.current) {
      return;
    }

    scheduleReload();

    return () => {
      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current);
        timerIdRef.current = null;
      }

      isReloadingRef.current = false;
    };
  }, [enabled, scheduleReload]);

  const triggerReload = () => {
    if (isReloadingRef.current) {
      return;
    }

    isReloadingRef.current = true;

    try {
      if (onBeforeReloadRef.current) {
        onBeforeReloadRef.current();
      }
      window.location.reload();
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: logging error in console
      console.error('Error during manual app reload:', error);
    }
  };

  const resetTimer = () => {
    if (timerIdRef.current) {
      clearTimeout(timerIdRef.current);
      timerIdRef.current = null;
    }

    isReloadingRef.current = false;
  };

  return {
    triggerReload,
    resetTimer,
    resetAndRestartTimer,
  };
};
